'use client';

import { Flex, freddyTheme, Skeleton } from '@coolblue-development/freddy';
import { debounceCallback } from '@utils/utils';
import Script from 'next/script';
import { useEffect, useState } from 'react';

import { GPT_SCRIPT_URL, RESIZE_DEBOUNCE_TIME } from './CoolblueAds.constants';
import { GooglePublisherCoolblueProps, KeyValueTargetingProps } from './CoolblueAds.types';
import { generateIdForSlotName, resizeAd } from './CoolblueAds.utils';

interface SlotRenderEndedEvent {
  slot: googletag.Slot | undefined;
  isEmpty: boolean;
}

let adSlots: Record<string, googletag.Slot> = {};

function InitializeGPT({ keyValueTargeting }: { keyValueTargeting?: KeyValueTargetingProps }) {
  // Reset tracking variables.
  adSlots = {};

  if (typeof window !== 'undefined') {
    // Ensure we can interact with the GPT command array.
    window.googletag = window.googletag || { cmd: [] };

    // Prepare GPT to display ads.
    googletag.cmd.push(() => {
      // Disable initial load, to precisely control when ads are requested.
      googletag.pubads().disableInitialLoad();

      if (keyValueTargeting) {
        Object.keys(keyValueTargeting).forEach(key => {
          googletag
            .pubads()
            .setTargeting(key, keyValueTargeting[key as keyof KeyValueTargetingProps]);
        });
      }

      // Enable SRA and services.
      googletag.pubads().enableSingleRequest();
      googletag.enableServices();
    });
  }

  return <Script async src={GPT_SCRIPT_URL} />;
}

function DefineAdSlot({
  adUnit,
  width,
  height,
  type,
  isFilter,
  children,
  uniqueId
}: GooglePublisherCoolblueProps) {
  const slotId = generateIdForSlotName(adUnit, type);
  const [styling, setStyling] = useState<{ transform: string } | undefined>(undefined);
  const [renderFallback, setRenderFallback] = useState<boolean>(false);
  const [skeletonLoading, setSkeletonLoading] = useState<boolean>(true);

  useEffect(() => {
    const handleResize = () => {
      setStyling(resizeAd(width, height, slotId, isFilter));
    };

    const debouncedResizeHandler = debounceCallback(handleResize, RESIZE_DEBOUNCE_TIME);

    window.addEventListener('resize', debouncedResizeHandler);

    // Initial resize of the ad.
    setStyling(resizeAd(width, height, slotId, isFilter));

    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    googletag.cmd.push(() => {
      // Clean up any existing slots with the same adUnit to prevent conflicts
      const existingSlots = googletag.pubads().getSlots();
      const conflictingSlots = existingSlots.filter(slot => slot.getAdUnitPath() === adUnit);

      if (conflictingSlots.length > 0) {
        googletag.destroySlots(conflictingSlots);
        // Clean up our tracking for these slots
        conflictingSlots.forEach(slot => {
          const elementId = slot.getSlotElementId();
          if (elementId && adSlots[elementId]) {
            delete adSlots[elementId];
          }
        });
      }

      // Also clean up our specific slot if it exists
      if (adSlots[slotId]) {
        googletag.destroySlots([adSlots[slotId]]);
        delete adSlots[slotId];
      }

      const slot = googletag.defineSlot(adUnit, [width, height], slotId);

      function onSlotRenderEnded(event: SlotRenderEndedEvent) {
        setSkeletonLoading(false);
        if (event.slot === undefined || event.isEmpty) {
          setRenderFallback(true);
          return;
        }
      }

      if (slot) {
        slot.addService(googletag.pubads());
        googletag.pubads().refresh([slot]);
        adSlots[slotId] = slot;

        googletag.pubads().addEventListener('slotRenderEnded', onSlotRenderEnded);

        const slots = googletag.pubads().getSlots();

        if (isFilter && slots.length == 0) {
          timeoutId = setTimeout(() => {
            setSkeletonLoading(false);
            setRenderFallback(true);
          }, 1500);
        }
      }
    });

    // Clean up the slot when the component is unloaded or re-renders.
    return () => {
      window?.removeEventListener('resize', debouncedResizeHandler);

      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      googletag.cmd.push(() => {
        if (adSlots[slotId]) {
          googletag.destroySlots([adSlots[slotId]]);
          delete adSlots[slotId];
        }
      });
    };
  }, [height, width, slotId, adUnit, isFilter, uniqueId]);

  return (
    <>
      {isFilter && skeletonLoading && (
        <Skeleton
          height={height}
          sx={{
            borderRadius: freddyTheme.borderRadius[100]
          }}
          id='ad-skeleton'
        />
      )}
      <Flex
        id={slotId}
        alignItems='center'
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          ...styling
        }}
      />
      {renderFallback ? children : null}
    </>
  );
}

export { DefineAdSlot, InitializeGPT };
